import { h }              from 'vue';
import Core               from '@/core/Core';
import BaseService        from '@/core/services/BaseService';
import Lead               from '@/domains/Leads/Lead';
import LeadsDAO           from '@/domains/Leads/LeadsDAO';
import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';


class LeadsService extends BaseService {

	/**
	 * Варианты приоритета лида.
	 * Не используем $core.dictionaries.priorityOptions — у того справочника
	 * другие коды (none/regular/important/critical), а у 100+ исторических
	 * лидов уже проставлены high/medium/low. Синхронизация требует миграции на бэке.
	 */

	static readonly priorityOptions = [
		{ id: 'high',   name: 'Высокий' },
		{ id: 'medium', name: 'Обычный' },
		{ id: 'low',    name: 'Низкий'  },
	];

	static readonly sourceOptions = [
		{ id: 'default',      name: 'Контактная форма'    },
		{ id: 'no-categorie', name: 'Не найдена категория' },
		{ id: 'get-catalog',  name: 'Запрос каталога'     },
		{ id: 'make-order',   name: 'Заявка на заказ'     },
		{ id: 'make-recall',  name: 'Обратный звонок'     },
		{ id: 'registration', name: 'Регистрация'         },
	];

	static async getListPaginated(
    params?: Parameters<typeof LeadsDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: Lead[]; pagination: RpcDataPagination }> {
		const { pagination, sort, ...filters } = params ?? {};
		const response = await LeadsDAO.getList(filters, pagination, sort);

		return {
			data:       Core.utils.hydrateArray(response.data, Lead.fromServer),
			pagination: response.meta.pagination,
		};
	}

	static async getList(
    params?: Parameters<typeof LeadsDAO.getList>[0]
  ): Promise<Lead[]> {
		return (await this.getListPaginated(params)).data;
	}

	static async add(lead: Lead): Promise<boolean> {
		const response = await LeadsDAO.add(lead);

		if (response.ok) {
			Core.showNotification.success('Лид создан');
		}

		return response.ok;
	}

	static async update(lead: Lead): Promise<boolean> {
		const response = await LeadsDAO.update(lead);

		if (response.ok) {
			Core.showNotification.success('Лид обновлён');
		}

		return response.ok;
	}

	static async delete(id: number): Promise<boolean> {
		const response = await LeadsDAO.delete(id);

		if (response.ok) {
			Core.showNotification.success('Лид удалён');
		}

		return response.ok;
	}

	/** ========================================================================================= */

	/**
	 * Человекочитаемое название источника лида.
	 */

	static getSourceLabel(lead: Lead): string {
		const option = LeadsService.sourceOptions.find(o => o.id === lead.source);
		return option?.name ?? 'Источник неизвестен';
	}

	/**
	 * VNode бейджа приоритета для отображения в реестре и других местах.
	 * CSS-классы high/medium/low управляют цветом — см. LeadsRegistry.scss.
	 */

	static getPriorityVNode(lead: Lead) {
		const option = LeadsService.priorityOptions.find(option => option.id === lead.priority);
		const label  = option?.name ?? 'Нет';

		const className: Record<string, boolean> = { 'priority-badge': true };
		if (lead.priority) {
			className[lead.priority] = true;
		}

		return h('span', {
			class:     className,
			innerHTML: label,
		});
	}
}

export default LeadsService;
